import React, { useId } from "react";
import { Controller, Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface CheckboxNewProps {
	/**
	 * Label shown next to the checkbox. A string is treated as an i18n key
	 * (unless `skipTranslation` is set); any other ReactNode is rendered as-is.
	 */
	label?: React.ReactNode;
	/**
	 * Optional helper text rendered as muted subtext beneath the label.
	 * Treated as an i18n key unless `skipTranslation` is set.
	 */
	hint?: string;
	name?: string;
	disabled?: boolean;
	/**
	 * Controlled checked state. Leave undefined for an uncontrolled checkbox
	 * (use `defaultChecked` instead).
	 */
	checked?: boolean;
	defaultChecked?: boolean;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	/**
	 * Convenience callback receiving just the boolean checked state.
	 */
	onCheckedChange?: (checked: boolean) => void;
	/**
	 * Optional error message (i18n key) rendered in red beneath the hint.
	 */
	errors?: string;
	/**
	 * Skip i18n translation of `label`/`hint`/`errors`.
	 */
	skipTranslation?: boolean;
	/** Custom className for the outer container. */
	containerClassName?: string;
	/** Custom className for the visual checkbox box. */
	boxClassName?: string;
	/** Custom className for the label text. */
	labelClassName?: string;
	/** Custom className for the hint text. */
	hintClassName?: string;
}

const CheckboxNew = React.forwardRef<HTMLInputElement, CheckboxNewProps>(
	(
		{
			label,
			hint,
			name,
			disabled,
			checked,
			defaultChecked,
			value,
			onChange,
			onCheckedChange,
			errors,
			skipTranslation = false,
			containerClassName,
			boxClassName,
			labelClassName,
			hintClassName,
		},
		ref
	) => {
		const { t } = useTranslation();
		const id = useId();

		const tr = (key?: string) => (key ? (skipTranslation ? key : t(key as any)) : "");

		function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
			onChange?.(e);
			onCheckedChange?.(e.target.checked);
		}

		return (
			<div className={cn("flex select-none", containerClassName)}>
				<label
					htmlFor={id}
					className={cn(
						"group flex items-start gap-2.5",
						disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
					)}
				>
					{/* Native input drives state/accessibility; visually hidden. */}
					<input
						id={id}
						ref={ref}
						type="checkbox"
						name={name}
						value={value}
						checked={checked}
						defaultChecked={defaultChecked}
						disabled={disabled}
						onChange={handleChange}
						className="peer sr-only"
					/>
					<span
						aria-hidden="true"
						className={cn(
							"mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px]",
							"border border-gray-300 bg-white text-white transition-colors",
							"peer-checked:border-brand peer-checked:bg-brand",
							"peer-focus-visible:ring-2 peer-focus-visible:ring-brand/40 peer-focus-visible:ring-offset-1",
							!disabled && "group-hover:border-brand",
							errors && "border-red-500",
							boxClassName
						)}
					>
						{/* White check is invisible on the white (unchecked) box and
						    becomes visible once the box turns brand-colored. */}
						<Check className="h-3 w-3" strokeWidth={3} />
					</span>

					{(label || hint) && (
						<span className="flex flex-col">
							{label && (
								<span
									className={cn(
										"text-sm font-medium leading-tight text-gray-800 transition-colors",
										!disabled && "group-hover:text-gray-900",
										labelClassName
									)}
								>
									{typeof label === "string" ? tr(label) : label}
								</span>
							)}
							{hint && (
								<span className={cn("mt-0.5 text-xs leading-snug text-gray-500", hintClassName)}>
									{tr(hint)}
								</span>
							)}
						</span>
					)}
				</label>

				{errors && (
					<span className="sr-only" role="alert">
						{tr(errors)}
					</span>
				)}
			</div>
		);
	}
);

CheckboxNew.displayName = "CheckboxNew";

export default CheckboxNew;

/**
 * React Hook Form wrapper for {@link CheckboxNew}.
 *
 * Threads `field` props through and calls both RHF's `onChange` and any
 * external `onCheckedChange`, mirroring the `AppSelectController` pattern.
 */
export interface CheckboxNewControllerProps extends Omit<CheckboxNewProps, "checked" | "onChange"> {
	control: Control<any>;
	field: string;
}

export const CheckboxNewController: React.FC<CheckboxNewControllerProps> = ({
	control,
	field,
	onCheckedChange,
	...props
}) => {
	return (
		<Controller
			control={control}
			name={field}
			render={({ field: { value, onChange, ref }, fieldState }) => (
				<CheckboxNew
					{...props}
					ref={ref}
					checked={!!value}
					onCheckedChange={(checked) => {
						onChange(checked);
						onCheckedChange?.(checked);
					}}
					errors={props.errors ?? (fieldState.error?.message as string | undefined)}
				/>
			)}
		/>
	);
};
